import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { Modal } from '@despachador/ui';
import { reset, startPractice, stopPractice, trigger, undo, usePanic } from './lib/panic';

/** How long the connection status is held to raise (or, active, to manage) the silent alert. */
const HOLD_MS = 3000;

/**
 * The connection status of the top bar, which is also the silent alert's trigger (PNC-001,
 * docs/panic.md). It reads like any status: holding it 3 s starts the 5 s grace («Actualizando… 5»,
 * one tap undoes it); once sent, the dot turns square and nothing else changes. Holding it again
 * while active opens a neutral sheet to turn it off. Only whoever holds the phone sees the 2 px
 * progress line under the bar.
 */
export function ConnectionStatus({ socketState, disabled }: { socketState: string; disabled?: boolean }) {
  const panic = usePanic();
  const [progress, setProgress] = useState(0);
  const [sheet, setSheet] = useState(false);
  const hold = useRef<{ start: number; frame: number } | null>(null);
  /** the click that ends a completed hold must not undo the grace it just started */
  const swallowClick = useRef(false);
  const reduced = useRef(window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  const label =
    {
      open: 'Central en línea',
      connecting: 'Reconectando…',
      closed: 'Sin conexión',
    }[socketState] ?? socketState;
  const grace = panic.phase === 'grace';
  const active = panic.phase === 'active';

  const stop = () => {
    if (hold.current) cancelAnimationFrame(hold.current.frame);
    hold.current = null;
    setProgress(0);
  };
  useEffect(() => stop, []);
  // an offer takes the whole screen: a hold in progress ends there
  useEffect(() => void (disabled && stop()), [disabled]);

  const begin = () => {
    if (hold.current || grace || disabled) return;
    const start = performance.now();
    const tick = () => {
      const p = Math.min(1, (performance.now() - start) / HOLD_MS);
      // reduced motion: three steps instead of a growing line
      setProgress(reduced.current ? Math.floor(p * 3) / 3 : p);
      if (p < 1) {
        hold.current = { start, frame: requestAnimationFrame(tick) };
        return;
      }
      stop();
      swallowClick.current = true;
      if (active) setSheet(true);
      else trigger('bar');
    };
    hold.current = { start, frame: requestAnimationFrame(tick) };
  };

  const onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    begin();
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
      e.preventDefault();
      begin();
    }
  };
  const onKeyUp = (e: KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') stop();
  };
  const onClick = () => {
    if (swallowClick.current) {
      swallowClick.current = false;
      return;
    }
    if (grace) undo();
  };

  const aria = grace
    ? `Actualizando, ${panic.grace}. Toca para cancelar`
    : active
      ? 'Estado de conexión: modo seguro. Mantén presionado 3 segundos para restablecer'
      : `Estado de conexión: ${label.replace('…', '').toLowerCase()}. Mantén presionado 3 segundos para enviar una alerta silenciosa`;

  return (
    <>
      <button
        type="button"
        className={`conn${progress > 0 ? ' holding' : ''}`}
        aria-label={aria}
        disabled={disabled}
        onPointerDown={onPointerDown}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onBlur={stop}
        onClick={onClick}
        onContextMenu={(e) => e.preventDefault()}
      >
        <span className={`dot ${grace ? 'grace' : socketState}${active ? ' square' : ''}`} aria-hidden /> {grace ? `Actualizando… ${panic.grace}` : label}
      </button>
      <span className="conn-progress" style={{ transform: `scaleX(${progress})` }} aria-hidden />
      {/* out of the top bar: its stacking context would leave the radio strip over the sheet */}
      {sheet &&
        createPortal(
          <Modal
            title="Conexión con la central"
            confirm
            onClose={() => setSheet(false)}
            footer={
              <>
                <button
                  type="button"
                  className="btn lg"
                  onClick={() => {
                    setSheet(false);
                    void reset();
                  }}
                >
                  Restablecer conexión normal
                </button>
                <button type="button" className="btn primary lg" data-autofocus onClick={() => setSheet(false)}>
                  Dejar así
                </button>
              </>
            }
          >
            <p>La conexión está en modo seguro. Restablécela solo si todo está bien.</p>
          </Modal>,
          document.body,
        )}
    </>
  );
}

/** Account → Security: what the silent alert is, how it works, and a practice that tells nobody. */
export function SilentAlertHelp() {
  const panic = usePanic();
  // leaving the page ends a practice half done
  useEffect(() => () => stopPractice(), []);
  return (
    <section className="menu-group" aria-labelledby="silent-alert-title">
      <h2 id="silent-alert-title" className="micro-title">
        Alerta silenciosa
      </h2>
      <div className="silent-alert page-pad stack">
        <p>Si estás en peligro, avisa a la central sin que nadie lo note. El viaje sigue igual y en tu pantalla no aparece nada en rojo.</p>
        <ol className="silent-steps">
          <li>
            <span>Mantén presionado <b>«Central en línea»</b> 3 segundos.</span>
          </li>
          <li>
            <span>Si fue sin querer, tócalo mientras dice <b>«Actualizando…»</b>.</span>
          </li>
          <li>
            <span>Sentirás una vibración y el punto será <b>cuadrado</b>: la central ya sabe.</span>
          </li>
          <li>
            <span>Para desactivarla, mantén presionado otra vez y elige <b>«Restablecer conexión normal»</b>.</span>
          </li>
        </ol>
        {panic.practice ? (
          <div className="practice-on" role="status">
            <p>
              <b>Práctica en curso.</b> Mantén presionado «Central en línea», arriba. No se avisa a nadie.
            </p>
            <button type="button" className="btn block" onClick={stopPractice}>
              Terminar la práctica
            </button>
          </div>
        ) : (
          <button type="button" className="btn block lg" disabled={panic.phase !== 'idle'} onClick={startPractice}>
            Practicar (no avisa a nadie)
          </button>
        )}
        {panic.practiced && (
          <p className="practice-done" role="status">
            Práctica completada
          </p>
        )}
        <p className="muted small">La central no te llamará ni te hablará por radio mientras la alerta esté activa. Te contactará cuando sea seguro.</p>
      </div>
    </section>
  );
}
